import { useState, useEffect } from 'react';
import { CreditCard, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { paymentService } from '../../services';
import { ErrorMessage } from '../common';

export default function PaymentGateway({
  amount,
  serviceType,
  serviceId,
  onSuccess,
  onError,
  customerDetails = {}
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Load Razorpay script using paymentService
    paymentService.loadRazorpayScript().catch((error) => {
      console.error('Failed to load Razorpay:', error);
      setError('Failed to load payment system. Please refresh the page.');
    });
  }, []);

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      await paymentService.processPayment(
        serviceType,
        serviceId,
        customerDetails,
        (result) => {
          setLoading(false);
          if (onSuccess) {
            onSuccess(result);
          }
        },
        (error) => {
          setLoading(false);
          setError(error.message || 'Payment failed. Please try again.');
          if (onError) {
            onError(error);
          }
        }
      );
    } catch (error) {
      console.error('Payment initiation failed:', error);
      setError(error.message || 'Failed to initiate payment. Please try again.');
      setLoading(false);
      if (onError) {
        onError(error);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="text-center mb-6">
        <CreditCard className="w-12 h-12 text-blue-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Secure Payment
        </h3>
        <p className="text-gray-600">
          Complete your payment securely with Razorpay
        </p>
      </div>

      {/* Amount Display */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total Amount:</span>
          <span className="text-2xl font-bold text-green-600">
            ₹{parseFloat(amount).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6">
          <ErrorMessage message={error} variant="inline" />
        </div>
      )}

      {/* Payment Button */}
      <button
        onClick={handlePayment}
        disabled={loading}
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
          loading
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Processing...</span>
          </div>
        ) : (
          <>
            <Lock className="w-5 h-5 inline mr-2" />
            Pay ₹{parseFloat(amount).toFixed(2)}
          </>
        )}
      </button>

      {/* Security Info */}
      <div className="mt-6 text-center">
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
          <Lock className="w-4 h-4" />
          <span>256-bit SSL encrypted payment</span>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Powered by Razorpay - India's most trusted payment gateway
        </p>
      </div>

      {/* Accepted Payment Methods */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3 text-center">
          We Accept
        </h4>
        <div className="flex justify-center items-center space-x-4">
          <div className="text-xs text-gray-500">
            Credit/Debit Cards, UPI, Net Banking, Wallets
          </div>
        </div>
      </div>
    </div>
  );
}